import { saveAs } from 'file-saver';
import { DateTime } from 'luxon';

import { addExceptionNotification, addSuccess } from '@mse-reg/app/modules/notification.slice';
import { addCommentToRegistrationInfo, removeCommentFromRegistrationInfo } from '@mse-reg/app/store';
import {
  AdvisorDto,
  CommentDto,
  ExcelColumn,
  FileResponse,
  FilterPresetDto,
  IAdvisorClient,
  IRegistrationEditClient,
  IRegistrationExportClient,
  IRegistrationFilterPresetClient,
  IStudentClient,
  ModuleCategoryDto,
  ModuleDto,
  ModuleRegistrationInfoDto,
  PhaseDto,
  RegistrationFilterSelectionDto,
  RegistrationState,
  RegistrationStateChangeDto,
  resolver,
  RoleDto,
  SelectionOfLong,
  SemesterDto,
  StaticRegistrationFilterDto,
  StudentDto,
  StudentPreviewDto,
  StudentType,
  UasDto,
} from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// State
const initialState = {
  loading: false,
  staticFiltersLoading: false,
  moduleFiltersLoading: false,
  fileImportFiltersLoading: false,
  presetsLoading: false,
  previewLoading: false,
  presets: [] as FilterPresetDto[],
  selectedPreset: null as FilterPresetDto | null,
  filterSelection: null as RegistrationFilterSelectionDto | null,
  selectableColumns: [] as ExcelColumn[],
  selectableProfiles: [] as SelectionOfLong[],
  selectableLabels: [] as SelectionOfLong[],
  selectableLocations: [] as SelectionOfLong[],
  selectableStates: [] as RegistrationState[],
  selectableFileImports: [] as SelectionOfLong[],
  selectableModuleExecutionCodes: [] as string[],
  preview: [] as ModuleRegistrationInfoDto[],
  previewLoaded: false,
  studentSearchLoading: false,
  advisorSearchLoading: false,
  studentSearchResult: [] as StudentPreviewDto[],
  studentSearchLoaded: false,
  studentSearchString: '',
  advisorSearchResult: [] as AdvisorDto[],
  advisorSearchLoaded: false,
  advisorSearchString: '',
};

export type ModuleRegistrationExportState = Readonly<typeof initialState>;

// Slice
const slice = createSlice({
  name: 'registrationExport',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setStaticFiltersLoading(state, action: PayloadAction<boolean>) {
      state.staticFiltersLoading = action.payload;
    },
    setModuleFiltersLoading(state, action: PayloadAction<boolean>) {
      state.moduleFiltersLoading = action.payload;
    },
    setFileImportFiltersLoading(state, action: PayloadAction<boolean>) {
      state.fileImportFiltersLoading = action.payload;
    },
    setPresetsLoading(state, action: PayloadAction<boolean>) {
      state.presetsLoading = action.payload;
    },
    setPresets(state, action: PayloadAction<FilterPresetDto[]>) {
      state.presets = action.payload;
    },
    setPreset(state, action: PayloadAction<FilterPresetDto | null>) {
      const preset = action.payload;
      if (!preset) {
        if (state.filterSelection) {
          state.filterSelection = { ...state.filterSelection, presetId: undefined };
        }
        state.selectedPreset = null;
        state.previewLoaded = false;
        return;
      }
      state.selectedPreset = preset;
      state.filterSelection = { ...preset.filter, presetId: preset.id };
      state.previewLoaded = false;
    },
    setStaticFilters(state, action: PayloadAction<StaticRegistrationFilterDto>) {
      const filters = action.payload;
      const columns = state.filterSelection?.columnNames?.length
        ? [...state.filterSelection.columnNames]
        : filters.defaultColumns.map((c) => c.propertyName);
      state.selectableColumns = filters.columns;
      state.selectableProfiles = filters.profiles;
      state.selectableStates = filters.states;
      state.selectableLocations = filters.locations;
      state.selectableLabels = filters.labels;
      state.filterSelection = {
        ...state.filterSelection,
        columnNames: columns,
      } as RegistrationFilterSelectionDto;
    },
    setModuleFilters(state, action: PayloadAction<string[]>) {
      state.selectableModuleExecutionCodes = action.payload;
    },
    setFileImportFilters(state, action: PayloadAction<SelectionOfLong[]>) {
      state.selectableFileImports = action.payload;
    },
    setFilterSelection(state, action: PayloadAction<RegistrationFilterSelectionDto>) {
      const filterSelection = action.payload;
      const columns = filterSelection?.columnNames?.length
        ? [...filterSelection.columnNames]
        : (state.selectableColumns?.map((c) => c.propertyName) ?? []);
      state.filterSelection = {
        ...action.payload,
        columnNames: columns,
      };
    },
    addUasFilter(state, action: PayloadAction<UasDto>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          uasIds: [...(state.filterSelection.uasIds ?? []), action.payload.id],
        };
      }
      state.previewLoaded = false;
    },
    removeUasFilter(state, action: PayloadAction<UasDto>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          uasIds: state.filterSelection.uasIds?.filter((id) => id !== action.payload.id) ?? [],
        };
      }
      state.previewLoaded = false;
    },
    addSemesterFilter(state, action: PayloadAction<SelectionOfLong>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          semesterIds: [...(state.filterSelection.semesterIds ?? []), action.payload.id],
          phaseId: undefined,
        };
      }
      state.previewLoaded = false;
    },
    removeSemesterFilter(state, action: PayloadAction<SelectionOfLong>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          semesterIds: state.filterSelection.semesterIds?.filter((id) => id !== action.payload.id) ?? [],
        };
      }
      state.previewLoaded = false;
    },
    addPhaseFilter(state, action: PayloadAction<number | undefined>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          phaseId: action.payload,
        };
      }
      state.previewLoaded = false;
    },
    addStateFilter(state, action: PayloadAction<RegistrationState>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          states: [...(state.filterSelection.states ?? []), action.payload],
        };
      }
      state.previewLoaded = false;
    },
    removeStateFilter(state, action: PayloadAction<RegistrationState>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          states: state.filterSelection.states?.filter((s) => s !== action.payload) ?? [],
        };
      }
      state.previewLoaded = false;
    },
    addColumn(state, action: PayloadAction<ExcelColumn>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          columnNames: [...(state.filterSelection.columnNames ?? []), action.payload.propertyName],
        };
      }
    },
    removeColumn(state, action: PayloadAction<ExcelColumn>) {
      if (state.filterSelection) {
        const newColumns = state.filterSelection.columnNames?.filter((id) => id !== action.payload.propertyName) ?? [];
        const columns =
          newColumns.length > 0 ? newColumns : (state.selectableColumns?.map((c) => c.propertyName) ?? []);
        state.filterSelection = {
          ...state.filterSelection,
          columnNames: columns,
        };
      }
    },
    addLabelFilter(state, action: PayloadAction<SelectionOfLong>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          labelIds: [...(state.filterSelection.labelIds ?? []), action.payload.id],
        };
      }
      state.previewLoaded = false;
    },
    removeLabelFilter(state, action: PayloadAction<SelectionOfLong>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          labelIds: state.filterSelection.labelIds?.filter((id) => id !== action.payload.id) ?? [],
        };
      }
      state.previewLoaded = false;
    },
    addProfileFilter(state, action: PayloadAction<SelectionOfLong>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          profileIds: [...(state.filterSelection.profileIds ?? []), action.payload.id],
        };
      }
      state.previewLoaded = false;
    },
    removeProfileFilter(state, action: PayloadAction<SelectionOfLong>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          profileIds: state.filterSelection.profileIds?.filter((id) => id !== action.payload.id) ?? [],
        };
      }
      state.previewLoaded = false;
    },
    addLocationFilter(state, action: PayloadAction<SelectionOfLong>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          locationIds: [...(state.filterSelection.locationIds ?? []), action.payload.id],
        };
      }
      state.previewLoaded = false;
    },
    removeLocationFilter(state, action: PayloadAction<SelectionOfLong>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          locationIds: state.filterSelection.locationIds?.filter((id) => id !== action.payload.id) ?? [],
        };
      }
      state.previewLoaded = false;
    },
    addStudentTypeFilter(state, action: PayloadAction<StudentType>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          studentTypes: [...(state.filterSelection.studentTypes ?? []), action.payload],
        };
      }
      state.previewLoaded = false;
    },
    removeStudentTypeFilter(state, action: PayloadAction<StudentType>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          studentTypes: state.filterSelection.studentTypes?.filter((t) => t !== action.payload) ?? [],
        };
      }
      state.previewLoaded = false;
    },
    setStudent(state, action: PayloadAction<{ id: string; display: string } | null>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          student: action.payload,
          advisor: undefined,
          moduleCategoryId: 0,
          moduleId: 0,
          moduleExecutionCode: undefined,
          locationIds: [],
        };
      }
      state.previewLoaded = false;
      state.studentSearchLoaded = false;
      state.studentSearchResult = [];
    },
    setAdvisor(state, action: PayloadAction<{ id: string; display: string } | null>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          advisor: action.payload,
          student: undefined,
          moduleCategoryId: 0,
          moduleId: 0,
          moduleExecutionCode: undefined,
          locationIds: [],
        };
      }
      state.previewLoaded = false;
      state.advisorSearchLoaded = false;
      state.advisorSearchResult = [];
    },
    setModuleCategory(state, action: PayloadAction<ModuleCategoryDto | null>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          moduleCategoryId: action.payload?.id ?? 0,
          moduleId: 0,
          student: undefined,
          advisor: undefined,
        };
      }
      state.previewLoaded = false;
    },
    setModule(state, action: PayloadAction<ModuleDto | null>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          moduleId: action.payload?.id ?? 0,
          moduleCategoryId: action.payload ? action.payload.categoryId : state.filterSelection.moduleCategoryId,
          student: undefined,
          advisor: undefined,
        };
      }
      state.previewLoaded = false;
    },
    setModuleExecutionCode(state, action: PayloadAction<string | null>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          moduleExecutionCode: action.payload ?? '',
          student: undefined,
          advisor: undefined,
        };
      }
      state.previewLoaded = false;
    },
    setFileImport(state, action: PayloadAction<SelectionOfLong | null>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          fileImport: action.payload ?? undefined,
        };
      }
      state.previewLoaded = false;
    },
    setStateAt(state, action: PayloadAction<DateTime | null>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          stateAt: action.payload as unknown as string,
        };
      }
      state.previewLoaded = false;
    },
    setNewerThen(state, action: PayloadAction<DateTime | null>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          newerThen: action.payload as unknown as string,
        };
      }
      state.previewLoaded = false;
    },
    setHideRegistrations(state, action: PayloadAction<boolean>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          hideRegistrations: action.payload,
        };
      }
      state.previewLoaded = false;
    },
    setShowDeRegistrations(state, action: PayloadAction<boolean>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          hideDeRegistrations: !action.payload,
        };
      }
      state.previewLoaded = false;
    },
    setHasComment(state, action: PayloadAction<boolean>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          hasComment: action.payload,
        };
      }
      state.previewLoaded = false;
    },
    setHasInternalComment(state, action: PayloadAction<boolean>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          hasInternalComment: action.payload,
        };
      }
      state.previewLoaded = false;
    },
    setHasCompensationForDisadvantages(state, action: PayloadAction<boolean>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          hasCompensationForDisadvantages: action.payload,
        };
      }
      state.previewLoaded = false;
    },
    setHasNoUasEmail(state, action: PayloadAction<boolean>) {
      if (state.filterSelection) {
        state.filterSelection = {
          ...state.filterSelection,
          hasNoUasEmail: action.payload,
        };
      }
      state.previewLoaded = false;
    },
    setPreview(state, action: PayloadAction<ModuleRegistrationInfoDto[]>) {
      state.preview = action.payload;
      state.previewLoaded = true;
    },
    setPreviewLoading(state, action: PayloadAction<boolean>) {
      state.previewLoading = action.payload;
    },
    setStudentSearchLoading(state, action: PayloadAction<boolean>) {
      state.studentSearchLoading = action.payload;
    },
    setStudentSearchResult(state, action: PayloadAction<StudentPreviewDto[]>) {
      state.studentSearchResult = action.payload;
      state.studentSearchLoaded = true;
    },
    changeStudentSearchString(state, action: PayloadAction<string>) {
      state.studentSearchString = action.payload;
      state.studentSearchResult = [];
      state.studentSearchLoaded = false;
    },
    setAdvisorSearchLoading(state, action: PayloadAction<boolean>) {
      state.advisorSearchLoading = action.payload;
    },
    setAdvisorSearchResult(state, action: PayloadAction<AdvisorDto[]>) {
      state.advisorSearchResult = action.payload;
      state.advisorSearchLoaded = true;
    },
    changeAdvisorSearchString(state, action: PayloadAction<string>) {
      state.advisorSearchString = action.payload;
      state.advisorSearchResult = [];
      state.advisorSearchLoaded = false;
    },
    addRegistrationComment(state, action: PayloadAction<CommentDto>) {
      state.preview = addCommentToRegistrationInfo(state.preview, action.payload);
    },
    removeRegistrationComment(state, action: PayloadAction<CommentDto>) {
      state.preview = removeCommentFromRegistrationInfo(state.preview, action.payload);
    },
    reset() {
      return initialState;
    },
  },
});

export const registrationExportReducer = slice.reducer;
export const registrationExportActions = slice.actions;

// Component-scoped actions factory
// This pattern passes componentDispatch from the component to allow local dispatch
export const initActions = (
  componentDispatch: any,
  registrationExportClient: IRegistrationExportClient,
  presetClient: IRegistrationFilterPresetClient,
  registrationEditClient: IRegistrationEditClient,
  studentClient: IStudentClient,
  advisorClient: IAdvisorClient,
) => {
  const { actions } = slice;

  // Helper to get state from thunk
  type ThunkFn<T = void> = (dispatch: any, getState: () => any) => Promise<T>;

  // Async thunks defined within initActions to capture client dependencies
  const exportAsync =
    (filterSelection: RegistrationFilterSelectionDto): ThunkFn<FileResponse | null> =>
    async (dispatch) => {
      dispatch(actions.setLoading(true));
      try {
        const result = await registrationExportClient.exportRegistrations(filterSelection);
        saveAs(result.data, result.fileName);
        dispatch(actions.setLoading(false));
        return result;
      } catch (e) {
        dispatch(addExceptionNotification(e));
        dispatch(actions.setLoading(false));
      }
      return null;
    };

  const updateModuleFilterAsync =
    (filter?: RegistrationFilterSelectionDto): ThunkFn<string[]> =>
    async (dispatch, getState) => {
      dispatch(actions.setModuleFiltersLoading(true));
      try {
        const theFilter = filter ?? getState().moduleRegistrationExport.filterSelection;
        const result = await registrationExportClient.getModuleExecutionCodes(theFilter);
        dispatch(actions.setModuleFilters(result));
        dispatch(actions.setModuleFiltersLoading(false));
        return result;
      } catch (e) {
        dispatch(addExceptionNotification(e));
        dispatch(actions.setModuleFiltersLoading(false));
      }
      return [];
    };

  const updateFileImportFilterAsync =
    (filter?: RegistrationFilterSelectionDto): ThunkFn =>
    async (dispatch, getState) => {
      dispatch(actions.setFileImportFiltersLoading(true));
      try {
        const theFilter = filter ?? getState().moduleRegistrationExport.filterSelection;
        const result = await registrationExportClient.getFileImportSelection(theFilter);
        dispatch(actions.setFileImportFilters(result));
        dispatch(actions.setFileImportFiltersLoading(false));
      } catch (e) {
        dispatch(addExceptionNotification(e));
        dispatch(actions.setFileImportFiltersLoading(false));
      }
    };

  const loadFiltersAsync = (): ThunkFn => async (dispatch) => {
    dispatch(actions.setStaticFiltersLoading(true));
    try {
      const result = await registrationExportClient.getStaticFilters();
      dispatch(actions.setStaticFilters(result));

      const a = dispatch(updateModuleFilterAsync());
      const b = dispatch(updateFileImportFilterAsync());
      await Promise.all([a, b]);

      dispatch(actions.setStaticFiltersLoading(false));
    } catch (e) {
      dispatch(addExceptionNotification(e));
      dispatch(actions.setStaticFiltersLoading(false));
    }
  };

  const loadPresetsAsync = (): ThunkFn<FilterPresetDto[]> => async (dispatch) => {
    dispatch(actions.setPresetsLoading(true));
    try {
      const result = await presetClient.list();
      dispatch(actions.setPresets(result));
      dispatch(actions.setPresetsLoading(false));
      return result;
    } catch (e) {
      dispatch(addExceptionNotification(e));
      dispatch(actions.setPresetsLoading(false));
    }
    return [];
  };

  const addUasFilterAsync =
    (uas: UasDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.addUasFilter(uas));
      await dispatch(updateFileImportFilterAsync());
    };

  const removeUasFilterAsync =
    (uas: UasDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.removeUasFilter(uas));
      const a = dispatch(updateModuleFilterAsync());
      const b = dispatch(updateFileImportFilterAsync());
      await Promise.all([a, b]);
    };

  const addSemesterFilterAsync =
    (semester: SelectionOfLong): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.addSemesterFilter(semester));
      const a = dispatch(updateModuleFilterAsync());
      const b = dispatch(updateFileImportFilterAsync());
      await Promise.all([a, b]);
    };

  const removeSemesterFilterAsync =
    (semester: SelectionOfLong): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.removeSemesterFilter(semester));
      const a = dispatch(updateModuleFilterAsync());
      const b = dispatch(updateFileImportFilterAsync());
      await Promise.all([a, b]);
    };

  const setPhaseFilterAsync =
    (phase: PhaseDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.addPhaseFilter(phase?.id ? phase.id : undefined));
      await dispatch(updateFileImportFilterAsync());
    };

  const setPresetAsync =
    (preset: FilterPresetDto | null): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setPreset(preset));
      const a = dispatch(updateModuleFilterAsync(preset?.filter));
      const b = dispatch(updateFileImportFilterAsync(preset?.filter));
      await Promise.all([a, b]);
    };

  const setModuleCategoryAsync =
    (moduleCategory: ModuleCategoryDto | null): ThunkFn =>
    async (dispatch, getState) => {
      const currentModuleId = getState().moduleRegistrationExport.filterSelection?.moduleId;
      const currentModule = getState().moduleSelection.modules.find((m: ModuleDto) => m.id === currentModuleId);
      const currentModuleExecutionCode = getState().moduleRegistrationExport.filterSelection?.moduleExecutionCode;

      dispatch(actions.setModuleCategory(moduleCategory));

      if (currentModule?.categoryId === moduleCategory?.id) {
        dispatch(actions.setModule(currentModule));
      }

      const result = await dispatch(updateModuleFilterAsync());
      if (currentModuleExecutionCode && result.includes(currentModuleExecutionCode)) {
        dispatch(actions.setModuleExecutionCode(currentModuleExecutionCode));
      } else if (result.length === 1) {
        dispatch(actions.setModuleExecutionCode(result[0]));
      }
    };

  const setModuleAsync =
    (module: ModuleDto | null): ThunkFn =>
    async (dispatch, getState) => {
      dispatch(actions.setModule(module));

      const currentModuleExecutionCode = getState().moduleRegistrationExport.filterSelection?.moduleExecutionCode;
      const result = await dispatch(updateModuleFilterAsync());
      if (currentModuleExecutionCode && result.includes(currentModuleExecutionCode)) {
        dispatch(actions.setModuleExecutionCode(currentModuleExecutionCode));
      } else if (result.length === 1) {
        dispatch(actions.setModuleExecutionCode(result[0]));
      }
    };

  const setModuleExecutionCode =
    (code: string | null): ThunkFn =>
    async (dispatch, getState) => {
      const currentModuleId = getState().moduleRegistrationExport.filterSelection?.moduleId;
      const splits = code?.split('_') ?? [];
      if (splits.length > 1) {
        const moduleCode = `${splits[0]}_${splits[1]}`;
        const currentModule = getState().moduleSelection.modules.find((m: ModuleDto) => m.code === moduleCode);
        if (currentModule && currentModuleId !== currentModule.id) {
          dispatch(actions.setModule(currentModule));
        } else if (!currentModule) {
          dispatch(actions.setModule(null));
        }
      }
      dispatch(actions.setModuleExecutionCode(code));
    };

  const loadPreviewAsync =
    (filterSelection: RegistrationFilterSelectionDto): ThunkFn<ModuleRegistrationInfoDto[] | null> =>
    async (dispatch) => {
      dispatch(actions.setPreviewLoading(true));
      dispatch(actions.setPreview([]));
      try {
        const result = await registrationExportClient.getRegistrationExportPreview(filterSelection);
        dispatch(actions.setPreview(result));
        dispatch(actions.setFilterSelection(filterSelection));
        dispatch(actions.setPreviewLoading(false));
        return result;
      } catch (e) {
        dispatch(addExceptionNotification(e));
        dispatch(actions.setPreviewLoading(false));
      }
      return null;
    };

  const searchStudentAsync =
    (searchString: string): ThunkFn<StudentPreviewDto[]> =>
    async (dispatch, getState) => {
      dispatch(actions.setStudentSearchLoading(true));
      try {
        const filter = getState().moduleRegistrationExport.filterSelection;
        const semesterIds = filter?.semesterIds;
        const uasIds = filter?.uasIds;
        const searchResult = await studentClient.search({ uasIds, semesterIds, searchString });
        dispatch(actions.setStudentSearchResult(searchResult));
        if (searchResult.length === 1) {
          dispatch(
            actions.setStudent({
              id: String(searchResult[0].id),
              display: `${searchResult[0].firstName} ${searchResult[0].familyName}`,
            }),
          );
        }
        dispatch(actions.setStudentSearchLoading(false));
        return searchResult;
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while searching.'));
        dispatch(actions.setStudentSearchLoading(false));
        return [];
      }
    };

  const searchAdvisorAsync =
    (searchString: string): ThunkFn<AdvisorDto[]> =>
    async (dispatch, getState) => {
      dispatch(actions.setAdvisorSearchLoading(true));
      try {
        const filter = getState().moduleRegistrationExport.filterSelection;
        const semesterIds = filter?.semesterIds;
        const uasIds = filter?.uasIds;
        const searchResult = await advisorClient.search({ uasIds, semesterIds, searchString });
        dispatch(actions.setAdvisorSearchResult(searchResult));
        if (searchResult.length === 1) {
          dispatch(
            actions.setAdvisor({
              id: String(searchResult[0].id),
              display: `${searchResult[0].firstName} ${searchResult[0].familyName}`,
            }),
          );
        }
        dispatch(actions.setAdvisorSearchLoading(false));
        return searchResult;
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while searching.'));
        dispatch(actions.setAdvisorSearchLoading(false));
        return [];
      }
    };

  const createFilterPresetAsync =
    (name: string, roles: RoleDto[]): ThunkFn<FilterPresetDto | null> =>
    async (dispatch, getState) => {
      dispatch(actions.setPresetsLoading(true));
      try {
        const filter = getState().moduleRegistrationExport.filterSelection;
        const id = await presetClient.add({
          filter,
          name,
          forRoles: roles.map((r) => r.roleType),
          id: 0,
          isOwner: true,
          template: undefined,
          templateId: undefined,
          fileName: undefined,
          owner: undefined,
        });
        const presets = await presetClient.list();
        dispatch(actions.setPresets(presets));
        const createdPreset = presets.find((p) => p.id === id);
        if (createdPreset) {
          await dispatch(setPresetAsync(createdPreset));
        }
        dispatch(actions.setPresetsLoading(false));
        return createdPreset ?? null;
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while saving the filter preset.'));
        dispatch(actions.setPresetsLoading(false));
      }
      return null;
    };

  const updateFilterPresetAsync =
    (preset: FilterPresetDto, name: string, roles: RoleDto[]): ThunkFn<FilterPresetDto | null> =>
    async (dispatch, getState) => {
      dispatch(actions.setPresetsLoading(true));
      try {
        const filter = getState().moduleRegistrationExport.filterSelection;
        await presetClient.update({
          ...preset,
          name,
          forRoles: roles.map((r) => r.roleType),
          filter,
        });
        const presets = await presetClient.list();
        dispatch(actions.setPresets(presets));
        const updatedPreset = presets.find((p) => p.id === preset.id);
        if (updatedPreset) {
          await dispatch(setPresetAsync(updatedPreset));
        }
        dispatch(actions.setPresetsLoading(false));
        return updatedPreset ?? null;
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while saving the filter preset.'));
        dispatch(actions.setPresetsLoading(false));
      }
      return null;
    };

  const deleteFilterPresetAsync =
    (preset: FilterPresetDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setPresetsLoading(true));
      try {
        await presetClient.delete(preset.id);
        const presets = await presetClient.list();
        dispatch(actions.setPresets(presets));
        await dispatch(setPresetAsync(null));
        dispatch(actions.setPresetsLoading(false));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while deleting the filter preset.'));
        dispatch(actions.setPresetsLoading(false));
      }
    };

  const resetFiltersAsync = (): ThunkFn => async (dispatch) => {
    dispatch(actions.reset());
    await dispatch(loadFiltersAsync());
    await dispatch(loadPresetsAsync());
  };

  const addCommentAsync =
    (comment: CommentDto): ThunkFn =>
    async (dispatch) => {
      try {
        const id = await registrationEditClient.addComment(comment);
        const newComment: CommentDto = { ...comment, id };
        dispatch(actions.addRegistrationComment(newComment));
        dispatch(addSuccess(`Successfully added comment.`));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while saving comment.'));
      }
    };

  const removeCommentAsync =
    (comment: CommentDto): ThunkFn =>
    async (dispatch) => {
      try {
        await registrationEditClient.deleteComment(comment.id);
        dispatch(actions.removeRegistrationComment(comment));
        dispatch(addSuccess(`Successfully deleted comment.`));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'An unknown error occurred while deleting comment.'));
      }
    };

  const getHistoryAsync = async (moduleRegistrationId: number): Promise<RegistrationStateChangeDto[]> => {
    return await registrationEditClient.getHistory(moduleRegistrationId);
  };

  // Helper to create student selection object
  const toStudentSelection = (student: StudentPreviewDto | null) =>
    student ? { id: String(student.id), display: `${student.firstName} ${student.familyName}` } : null;

  // Helper to create advisor selection object
  const toAdvisorSelection = (advisor: AdvisorDto | null) =>
    advisor ? { id: String(advisor.id), display: `${advisor.firstName} ${advisor.familyName}` } : null;

  return Object.freeze({
    exportAsync: async (filterSelection: RegistrationFilterSelectionDto): Promise<FileResponse | null> =>
      await componentDispatch(exportAsync(filterSelection)),
    loadFiltersAsync: async (): Promise<void> => await componentDispatch(loadFiltersAsync()),
    loadPresetsAsync: async (): Promise<FilterPresetDto[]> => await componentDispatch(loadPresetsAsync()),
    addUasFilterAsync: async (uas: UasDto): Promise<void> => await componentDispatch(addUasFilterAsync(uas)),
    removeUasFilterAsync: async (uas: UasDto): Promise<void> => await componentDispatch(removeUasFilterAsync(uas)),
    addSemesterFilterAsync: async (semester: SemesterDto): Promise<void> =>
      await componentDispatch(addSemesterFilterAsync({ id: semester.id, display: semester.name })),
    removeSemesterFilterAsync: async (semester: SemesterDto): Promise<void> =>
      await componentDispatch(removeSemesterFilterAsync({ id: semester.id, display: semester.name })),
    addColumn: (column: ExcelColumn): void => componentDispatch(actions.addColumn(column)),
    removeColumn: (column: ExcelColumn): void => componentDispatch(actions.removeColumn(column)),
    setPhaseFilterAsync: async (phase: PhaseDto): Promise<void> => await componentDispatch(setPhaseFilterAsync(phase)),
    addStateFilter: (state: RegistrationState): void => componentDispatch(actions.addStateFilter(state)),
    removeStateFilter: (state: RegistrationState): void => componentDispatch(actions.removeStateFilter(state)),
    addLabelFilter: (label: SelectionOfLong): void => componentDispatch(actions.addLabelFilter(label)),
    removeLabelFilter: (label: SelectionOfLong): void => componentDispatch(actions.removeLabelFilter(label)),
    addProfileFilter: (profile: SelectionOfLong): void => componentDispatch(actions.addProfileFilter(profile)),
    removeProfileFilter: (profile: SelectionOfLong): void => componentDispatch(actions.removeProfileFilter(profile)),
    addLocationFilter: (location: SelectionOfLong): void => componentDispatch(actions.addLocationFilter(location)),
    removeLocationFilter: (location: SelectionOfLong): void =>
      componentDispatch(actions.removeLocationFilter(location)),
    setStudent: (student: StudentPreviewDto): void =>
      componentDispatch(actions.setStudent(toStudentSelection(student))),
    setAdvisor: (advisor: AdvisorDto): void => componentDispatch(actions.setAdvisor(toAdvisorSelection(advisor))),
    setModuleCategoryAsync: async (moduleCategory: ModuleCategoryDto): Promise<void> =>
      await componentDispatch(setModuleCategoryAsync(moduleCategory)),
    setModuleAsync: async (module: ModuleDto): Promise<void> => await componentDispatch(setModuleAsync(module)),
    setModuleExecutionCode: (code: string): void => componentDispatch(setModuleExecutionCode(code)),
    setFileImport: (fileImport: SelectionOfLong): void => componentDispatch(actions.setFileImport(fileImport)),
    setStateAt: (date: DateTime | null): void => componentDispatch(actions.setStateAt(date)),
    setNewerThen: (date: DateTime | null): void => componentDispatch(actions.setNewerThen(date)),
    setPresetAsync: async (preset: FilterPresetDto): Promise<void> => await componentDispatch(setPresetAsync(preset)),
    setHideRegistrations: (hide: boolean): void => componentDispatch(actions.setHideRegistrations(hide)),
    setShowDeRegistrations: (show: boolean): void => componentDispatch(actions.setShowDeRegistrations(show)),
    setHasComment: (hasComment: boolean): void => componentDispatch(actions.setHasComment(hasComment)),
    setHasInternalComment: (hasInternalComment: boolean): void =>
      componentDispatch(actions.setHasInternalComment(hasInternalComment)),
    setHasCompensationForDisadvantages: (hasCompensationForDisadvantages: boolean): void =>
      componentDispatch(actions.setHasCompensationForDisadvantages(hasCompensationForDisadvantages)),
    setHasNoUasEmail: (hasNoUasEmail: boolean): void => componentDispatch(actions.setHasNoUasEmail(hasNoUasEmail)),
    loadPreviewAsync: async (
      filterSelection: RegistrationFilterSelectionDto,
    ): Promise<ModuleRegistrationInfoDto[] | null> => await componentDispatch(loadPreviewAsync(filterSelection)),
    searchStudentAsync: async (searchString: string): Promise<StudentDto[]> =>
      await componentDispatch(searchStudentAsync(searchString)),
    searchAdvisorAsync: async (searchString: string): Promise<AdvisorDto[]> =>
      await componentDispatch(searchAdvisorAsync(searchString)),
    createFilterPresetAsync: async (name: string, roles: RoleDto[]): Promise<FilterPresetDto | null> =>
      await componentDispatch(createFilterPresetAsync(name, roles)),
    updateFilterPresetAsync: async (preset: FilterPresetDto, name: string, roles: RoleDto[]): Promise<void> =>
      await componentDispatch(updateFilterPresetAsync(preset, name, roles)),
    deleteFilterPresetAsync: async (preset: FilterPresetDto): Promise<void> =>
      await componentDispatch(deleteFilterPresetAsync(preset)),
    resetFiltersAsync: async (): Promise<void> => await componentDispatch(resetFiltersAsync()),
    changeAdvisorSearchString: (searchString: string) =>
      componentDispatch(actions.changeAdvisorSearchString(searchString)),
    changeStudentSearchString: (searchString: string) =>
      componentDispatch(actions.changeStudentSearchString(searchString)),
    addCommentAsync: async (comment: CommentDto) => await componentDispatch(addCommentAsync(comment)),
    removeCommentAsync: async (comment: CommentDto) => await componentDispatch(removeCommentAsync(comment)),
    addStudentTypeFilter: (studentType: StudentType) => componentDispatch(actions.addStudentTypeFilter(studentType)),
    removeStudentTypeFilter: (studentType: StudentType) =>
      componentDispatch(actions.removeStudentTypeFilter(studentType)),
    getHistoryAsync,
  });
};

export default initActions;

export type ModuleRegistrationExportActions = ReturnType<typeof initActions>;

export const registrationExportModule = {
  initActions: (dispatch: any) =>
    initActions(
      dispatch,
      resolver.resolve<IRegistrationExportClient>('IRegistrationExportClient'),
      resolver.resolve<IRegistrationFilterPresetClient>('IRegistrationFilterPresetClient'),
      resolver.resolve<IRegistrationEditClient>('IRegistrationEditClient'),
      resolver.resolve<IStudentClient>('IStudentClient'),
      resolver.resolve<IAdvisorClient>('IAdvisorClient'),
    ),
  reducer: registrationExportReducer,
};
